import React from 'react'
import type { ICellRendererParams } from 'ag-grid-community'
import { Vertical, Texto, Horizontal } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import type { Match, ProductTransaction } from '../../../../../../api/useSupplierReconciliation/types/match'

interface ProductsCellRendererProps extends ICellRendererParams<Match> {
  value: string[]
}

interface ProductRatio {
  name: string
  amount?: number
  volume: number
}

function ProductPill({ ratio }: { ratio: ProductRatio }) {
  if (!ratio) return null

  const formattedVolume = ratio.volume > 0 ? `${Math.round(ratio.volume).toLocaleString()} L` : ratio.name

  return (
    <Tooltip placement='top' title={formattedVolume}>
      <div>
        <Horizontal alignItems='stretch' className='mr-2 mb-1' style={{ overflow: 'visible' }}>
          {ratio.amount && ratio.amount > 0 && (
            <Vertical
              verticalCenter
              borderRadius='6px 0 0 6px'
              className='px-1'
              style={{
                minWidth: 16,
                background: 'var(--gray-700)',
                height: '100%',
                border: 'solid 1px var(--gray-700)',
              }}
            >
              <Texto style={{ color: 'var(--gray-100)' }} weight='bold' align='center' category='label'>
                {ratio.amount}
              </Texto>
            </Vertical>
          )}

          <Vertical
            verticalCenter
            className='px-1'
            style={{
              fontWeight: 600,
              textTransform: 'uppercase',
              background: 'var(--bg-2)',
              borderRadius: ratio.amount && ratio.amount > 0 ? '0 6px 6px 0' : '6px',
              height: '100%',
              border: 'solid 1px var(--gray-300)',
              whiteSpace: 'nowrap',
            }}
          >
            <Texto category='label'>{ratio.name}</Texto>
          </Vertical>
        </Horizontal>
      </div>
    </Tooltip>
  )
}

function getProductRatios(match: Match): ProductRatio[] {
  // Try to get product ratios from detailed transaction data if available
  const allTransactions: ProductTransaction[] = []

  // Check if we have details with product_transactions
  if (match.details && match.details.length > 0) {
    match.details.forEach((detail) => {
      // Get product transactions from details - these might be from MatchInvoice or MatchBol
      // Note: details doesn't have product_transactions in the type, but the actual API might include them
      const detailTransactions = (detail as any)?.product_transactions
      if (detailTransactions && Array.isArray(detailTransactions)) {
        allTransactions.push(...detailTransactions)
      }
    })
  }

  if (allTransactions.length > 0) {
    // Convert product transactions to ratios
    const productMap = new Map<string, { totalVolume: number; count: number }>()

    allTransactions.forEach((transaction) => {
      const productName = transaction.product_name
      const volume = transaction.gross_volume || transaction.net_volume || 0

      if (productMap.has(productName)) {
        const existing = productMap.get(productName)!
        existing.totalVolume += volume
        existing.count += 1
      } else {
        productMap.set(productName, { totalVolume: volume, count: 1 })
      }
    })

    return Array.from(productMap.entries()).map(([name, data]) => ({
      name,
      amount: data.totalVolume > 0 ? Math.round(data.totalVolume / 1000) : undefined,
      volume: data.totalVolume,
    }))
  }

  // Fallback: use product names without volume data
  if (match.products && match.products.length > 0) {
    return match.products.map((productName) => ({
      name: productName.replace(/^(BOL-|INV-)/i, ''), // Remove prefixes if present
      amount: undefined, // No volume data available
      volume: 0,
    }))
  }

  return []
}

export function ProductsCellRenderer({ data }: ProductsCellRendererProps) {
  if (!data) return null

  const ratios = getProductRatios(data)

  if (ratios.length === 0) {
    return null
  }

  return (
    <div className='flex' style={{ flexWrap: 'wrap' }}>
      {ratios.map((ratio, index) => (
        <ProductPill key={index} ratio={ratio} />
      ))}
    </div>
  )
}
